import { createConsumer, type Consumer, type Subscription } from "@rails/actioncable"

import type { ToastKind } from "@/lib/toast"

/** Events broadcast to everyone in an account (AccountChannel). */
export type AccountEvent =
  | { type: "activity.changed"; document_id?: number }
  | { type: "bank_statement_file.changed"; bank_statement_file_id: number }
  | { type: "bank_statement_upload.changed"; bank_statement_upload_id: number; bank_statement_file_id: number }
  | { type: "bank_sync.changed"; bank_sync_id: number }
  | { type: "counterparties.changed" }
  | { type: "document.changed"; document_id: number }
  | { type: "sales_document.changed"; sales_document_id: number }
  | { type: "payments.changed" }
  | { type: "company_identity_confirmation.needed"; path: string }
  | { type: "incoming_emails.changed"; incoming_email_id: number | null }

/** Events broadcast to one user (UserChannel). */
export type UserEvent =
  | { type: "flash"; kind: ToastKind; message: string }
  | { type: "month_end_export.changed"; month: string }
  | { type: "mobile_device.connected" }

export type ChannelEvents = {
  AccountChannel: AccountEvent
  UserChannel: UserEvent
}

export type ChannelName = keyof ChannelEvents

type Listener<Channel extends ChannelName> = {
  onEvent: (event: ChannelEvents[Channel]) => void
  onConnected?: () => void
}

type OpenChannel = {
  subscription: Subscription
  listeners: Set<Listener<ChannelName>>
  connected: boolean
}

let consumer: Consumer | null = null
const openChannels = new Map<ChannelName, OpenChannel>()

function openChannel(name: ChannelName): OpenChannel {
  consumer ??= createConsumer()

  const channel: OpenChannel = {
    listeners: new Set(),
    connected: false,
    subscription: consumer.subscriptions.create(name, {
      connected: () => {
        channel.connected = true
        for (const listener of channel.listeners) listener.onConnected?.()
      },
      disconnected: () => {
        channel.connected = false
      },
      received: (data: unknown) => {
        if (typeof data !== "object" || data === null || !("type" in data)) return
        for (const listener of channel.listeners) listener.onEvent(data as ChannelEvents[ChannelName])
      },
    }),
  }

  openChannels.set(name, channel)
  return channel
}

/**
 * Listens to a channel until the returned cleanup function is called. All listeners share one
 * server subscription per channel. `onConnected` also runs after a reconnect, so callers can
 * refetch what they missed while offline.
 */
export function subscribe<Channel extends ChannelName>(
  name: Channel,
  onEvent: (event: ChannelEvents[Channel]) => void,
  onConnected?: () => void,
): () => void {
  const channel = openChannels.get(name) ?? openChannel(name)
  const listener = { onEvent, onConnected } as Listener<ChannelName>
  channel.listeners.add(listener)
  if (channel.connected) onConnected?.()

  return () => {
    channel.listeners.delete(listener)
    if (channel.listeners.size > 0) return

    channel.subscription.unsubscribe()
    openChannels.delete(name)
  }
}
